import { CALENDAR_CURRENCIES } from "@/lib/businessRules/calendarBusinessRule";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import {
  BOND_OPTION_TYPES,
  OPTION_DELIVERIES,
  OPTION_EXERCISE_TYPES,
  TradeDelivery,
  TradeOptionStyle,
  TradeOptionType,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import {
  returnBondOptionCalendar,
  returnBondOptionCurrency,
  returnBondOptionPaymentCurrency,
} from "../helper/bondOptionTicketHelper";
import {
  ITicketFieldsProps,
  TICKET_EMPTY_VALUE,
} from "../helper/ticketSharedHelper";
import TicketNumberInput from "../ticketFields/TicketNumberInput";
import TicketTenorDateInput from "../ticketFields/TicketTenorDateInput";
import TicketRow from "../ticketFields/TicketRow";
import TicketSelect from "../ticketFields/TicketSelect";
import { useBondOptionTicket } from "./useBondOptionTicket";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";

// The option terms. Becomes the Trade Creation Security Details panel content.

const BondOptionDetailFields = ({
  ticketId,
  highlightedKeys,
}: ITicketFieldsProps) => {
  const { ticket, updateBondOptionTicket, underlying, dateIssue, requiredKeys } =
    useBondOptionTicket(ticketId);

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);
  // Shown straight away rather than on a Send hover - a date in the wrong order is wrong now.
  const dateErrorFor = (key: string) =>
    dateIssue?.key === key ? dateIssue.message : "";

  // The underlying's own currency is always offered, even if we cannot calendar it.
  const underlyingCurrency = returnBondOptionCurrency(underlying);
  const paymentCurrencies = Array.from(
    new Set([underlyingCurrency, ...CALENDAR_CURRENCIES].filter(Boolean)),
  ).sort();
  const paymentCurrency = returnBondOptionPaymentCurrency(ticket, underlying);

  return (
    <div
      data-testid="BondOptionDetailFieldsContainer"
      id="BondOptionDetailFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Option Type"
        testId="BondOptionTicketOptionTypeRow"
        isHighlighted={isMissing("optionType")}
        isRequired={isRequired("optionType")}
      >
        <TicketSelect<TradeOptionType>
          testId="BondOptionTicketOptionTypeInput"
          value={ticket.optionType}
          options={BOND_OPTION_TYPES}
          onChange={(optionType) => updateBondOptionTicket({ optionType })}
        />
      </TicketRow>

      <TicketRow
        label="Expiry Date"
        testId="BondOptionTicketExpiryDateRow"
        isHighlighted={isMissing("expiryDate")}
        isRequired={isRequired("expiryDate")}
        error={dateErrorFor("expiryDate")}
      >
        <TicketTenorDateInput
          testId="BondOptionTicketExpiryDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.BondOption, ticket)}
          value={ticket.expiryDate}
          tenorStart={ticket.tradeDate}
          onChange={(expiryDate) => updateBondOptionTicket({ expiryDate })}
        />
      </TicketRow>

      {/* Optional at the API, so no highlight. Price strike only for now (spec known gap),
          so Strike Quotation is not shown and the notation is the box's label. */}
      <TicketRow label="Strike" testId="BondOptionTicketStrikeRow">
        <TicketNumberInput
          testId="BondOptionTicketStrikeInput"
          value={ticket.strike}
          placeholder="Price"
          onCommit={(strike) => updateBondOptionTicket({ strike })}
        />
      </TicketRow>

      <TicketRow label="Exercise Type" testId="BondOptionTicketExerciseTypeRow">
        <TicketSelect<TradeOptionStyle>
          testId="BondOptionTicketExerciseTypeInput"
          value={ticket.optionStyle}
          options={OPTION_EXERCISE_TYPES}
          includeEmptyOption={false}
          onChange={(optionStyle) => updateBondOptionTicket({ optionStyle })}
        />
      </TicketRow>

      <TicketRow label="Fixing Type" testId="BondOptionTicketFixingTypeRow">
        <TicketSelect<TradeDelivery>
          testId="BondOptionTicketFixingTypeInput"
          value={ticket.delivery}
          options={OPTION_DELIVERIES}
          includeEmptyOption={false}
          onChange={(delivery) => updateBondOptionTicket({ delivery })}
        />
      </TicketRow>

      <TicketRow label="Trade Date" testId="BondOptionTicketTradeDateRow">
        <div className="text-xs dark:text-gray-200">
          {ticket.tradeDate
            ? returnFormattedDate(ticket.tradeDate)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Underlying Ccy" testId="BondOptionTicketCurrencyRow">
        <div className="text-xs dark:text-gray-200">
          {underlyingCurrency || TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      {/* Defaults to the underlying's currency but stays editable: 147 of 586 real bond
          options settle in a different one. */}
      <TicketRow label="Payment Ccy" testId="BondOptionTicketPaymentCurrencyRow">
        <TicketSelect<string>
          testId="BondOptionTicketPaymentCurrencyInput"
          // The EFFECTIVE currency, so the row always shows what will be sent.
          value={paymentCurrency}
          options={paymentCurrencies}
          placeholder="Select ..."
          onChange={(currencyPayment) =>
            updateBondOptionTicket({ currencyPayment })
          }
        />
      </TicketRow>

      {/* Follows the PAYMENT currency - the calendar is the settlement business centre. */}
      <TicketRow label="Calendar" testId="BondOptionTicketCalendarRow">
        <div className="text-xs dark:text-gray-200">
          {paymentCurrency
            ? returnBondOptionCalendar(ticket, underlying)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>
    </div>
  );
};

export default BondOptionDetailFields;
